import { applyScope } from './scope.helpers';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers statiques
// ─────────────────────────────────────────────────────────────────────────────

export interface ActionDef {
	/** Classe glyphicon ou fa, ex: 'glyphicon glyphicon-pencil' ou 'fa fa-edit' */
	icon: string;
	/** Clé i18n ou texte du tooltip (utilisé avec $translate.instant si disponible) */
	title: string;
	/** Si false ou si la fonction retourne false, le bouton n'est pas affiché */
	visible?: boolean | ((rowData: unknown) => boolean);
	/** Si true ou si la fonction retourne true, le bouton est désactivé */
	disabled?: boolean | ((rowData: unknown) => boolean);
	/** Callback appelé au clic — reçoit les données de la ligne */
	onClick: (rowData: unknown) => void;
}

/**
 * Génère un formatter Tabulator pour une colonne d'actions (boutons icônes).
 *
 * Gère automatiquement e.stopPropagation() et wrapping $scope.$apply via
 * la fonction applyFn fournie.
 *
 * @example
 * columns: [
 *   {
 *     title: 'Actions', field: 'id', width: 140, headerSort: false, resizable: false,
 *     formatter: MassiaTabulator.makeActionColumn($scope, [
 *       { icon: 'glyphicon glyphicon-search', title: 'Visualiser', onClick: (d) => vm.selectFacture(d) },
 *       { icon: 'glyphicon glyphicon-pencil', title: 'Éditer', visible: (d) => d.isModifiable, onClick: (d) => vm.edit(d) },
 *     ])
 *   }
 * ]
 */
export function makeActionColumn($scope: unknown, actions: ActionDef[]): (cell: unknown) => HTMLElement {
	return function (cell: unknown): HTMLElement {
		const data = (cell as { getData: () => unknown }).getData();
		const wrap = document.createElement('div');
		wrap.className = 'btn-group-xs';

		actions.forEach((action) => {
			// Visibilité
			const isVisible =
				typeof action.visible === 'function'
					? action.visible(data)
					: action.visible !== false && action.visible !== undefined
						? action.visible
						: true;
			if (!isVisible) return;

			// Disabled
			const isDisabled = typeof action.disabled === 'function' ? action.disabled(data) : !!action.disabled;

			const btn = document.createElement('button');
			btn.className = 'btn btn-xs btn-link padding-0-2';
			btn.title = action.title;
			btn.disabled = isDisabled;
			btn.innerHTML = `<span class="${action.icon}"></span>`;
			btn.addEventListener('click', function (e: MouseEvent) {
				e.stopPropagation();
				applyScope($scope as ng.IScope | undefined, () => action.onClick(data));
			});
			wrap.appendChild(btn);
		});

		return wrap;
	};
}

export interface CheckboxColumnOptions {
	/** Champ du data qui porte l'état coché */
	field: string;
	/** Si la fonction retourne false, la checkbox n'est pas affichée */
	enabledWhen?: (rowData: unknown) => boolean;
	/** Callback appelé quand la checkbox change. Reçoit les données et le nouvel état */
	onChange: (rowData: unknown, checked: boolean, $scope: unknown) => void;
}

/**
 * Génère un formatter Tabulator pour une colonne checkbox simple.
 *
 * @example
 * {
 *   title: '...', field: 'isChecked', width: 65, hozAlign: 'center', headerSort: false,
 *   formatter: MassiaTabulator.makeCheckboxColumn($scope, {
 *     field: 'isChecked',
 *     enabledWhen: (d) => d.isModifiable,
 *     onChange: (d, checked) => { d.isChecked = checked; }
 *   })
 * }
 */
export function makeCheckboxColumn($scope: unknown, options: CheckboxColumnOptions): (cell: unknown) => HTMLElement | string {
	return function (cell: unknown): HTMLElement | string {
		const data = (cell as { getData: () => Record<string, unknown> }).getData();
		if (options.enabledWhen && !options.enabledWhen(data)) return '';

		const cb = document.createElement('input');
		cb.type = 'checkbox';
		cb.checked = !!data[options.field];
		cb.addEventListener('change', function () {
			applyScope($scope as ng.IScope | undefined, () => options.onChange(data, cb.checked, $scope));
		});
		return cb;
	};
}

export interface IconColumnOptions {
	/** Classe(s) CSS de l'icône, ex: 'glyphicon glyphicon-print' */
	icon: string;
	/** Texte du title/tooltip (verbatim, non traduit ici) */
	title?: string;
	/** Condition d'affichage de l'icône — si absent, utilise la valeur de la cellule */
	enabledWhen?: (rowData: unknown) => boolean;
	getTitle?: (rowData: unknown) => string | undefined;
}

/**
 * Génère un formatter Tabulator pour une colonne icône conditionnelle.
 *
 * @example
 * {
 *   title: '<span class="glyphicon glyphicon-print"></span>',
 *   field: 'isAlreadyPrint', width: 45, hozAlign: 'center',
 *   formatter: MassiaTabulator.makeIconColumn({
 *     icon: 'glyphicon glyphicon-print',
 *     title: 'Imprimée',
 *     enabledWhen: (d) => d.isAlreadyPrint,
 *   })
 * }
 */
export function makeIconColumn(options: IconColumnOptions): (cell: unknown) => string {
	return function (cell: unknown): string {
		const data = (cell as { getData: () => unknown }).getData();
		const show = options.enabledWhen != null ? options.enabledWhen(data) : !!(cell as { getValue: () => unknown }).getValue();
		if (!show) return '';
		const titleStr = options.getTitle ? options.getTitle(data) : options.title;
		const titleAttr = titleStr ? ` title="${titleStr}"` : '';
		return `<span class="${options.icon}"${titleAttr}></span>`;
	};
}

/**
 * Namespace regroupant les helpers statiques pour faciliter l'import.
 *
 * @example
 * import { MassiaTabulator } from '../../libs/tabulator/tabulator.controller';
 * formatter: MassiaTabulator.makeActionColumn($scope, [...])
 */
export const MassiaTabulatorHelpers = {
	makeActionColumn,
	makeCheckboxColumn,
	makeIconColumn
};
